import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, UploadCloud, CheckCircle2, FileText, Lock } from "lucide-react";

const FIRM_FIELDS = [
  ["legal_name", "Firm legal name"], ["address", "Firm address"], ["primary_contact", "Primary contact"],
  ["authorized_contacts", "Additional authorized contacts"], ["practice_areas", "Primary practice areas"],
  ["office_locations", "Office locations"], ["attorneys", "Approx. number of attorneys"], ["intake_employees", "Approx. number of intake employees"],
];
const VENDOR_FIELDS = [
  ["name", "Vendor / agency name"], ["service", "Service provided"], ["monthly_fee", "Monthly fee"], ["ad_spend", "Advertising spend"],
  ["contract_start", "Contract start date"], ["contract_renewal", "Contract renewal date"], ["contact", "Primary vendor contact"],
  ["channels", "Marketing channels managed"], ["metrics", "Reported performance metrics"], ["concerns", "Known concerns"],
];
const PERF_FIELDS = [
  ["monthly_leads", "Approx. monthly lead volume"], ["monthly_qualified", "Approx. monthly qualified-conversation volume"],
  ["monthly_consultations", "Approx. monthly consultation volume"], ["monthly_retained", "Approx. monthly retained-matter volume"],
  ["source_tracking", "Is source tracking currently used?"], ["match_vendor", "Can retained matters be matched to a specific vendor?"],
  ["match_revenue", "Can revenue be matched to a specific marketing source?"], ["crm", "Intake software or CRM currently used"],
  ["barriers", "Primary barriers to accurate reporting"],
];

export default function BaselineIntake() {
  const { token } = useParams();
  const [state, setState] = useState("loading");
  const [info, setInfo] = useState(null);
  const [firm, setFirm] = useState({});
  const [vendors, setVendors] = useState([{}, {}, {}]);
  const [perf, setPerf] = useState({});
  const [comments, setComments] = useState("");
  const [cert, setCert] = useState(false);
  const [docs, setDocs] = useState([]);
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    document.title = "Confidential Intake — RetainProof Baseline Review";
    api.get(`/public/baseline/intake/${token}`).then((r) => {
      setInfo(r.data); setDocs(r.data.documents || []);
      setState(r.data.status === "intake_submitted" ? "submitted" : "ready");
      api.post("/public/baseline/track", { event: "intake_start", session_id: token }).catch(() => {});
    }).catch((e) => setState(e.response?.status === 403 ? "unpaid" : "notfound"));
  }, [token]);

  const upFirm = (k) => (e) => setFirm({ ...firm, [k]: e.target.value });
  const upVendor = (i, k) => (e) => setVendors((v) => v.map((x, idx) => idx === i ? { ...x, [k]: e.target.value } : x));
  const upPerf = (k) => (e) => setPerf({ ...perf, [k]: e.target.value });

  const uploadFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    const fd = new FormData(); fd.append("file", file);
    try {
      await api.post(`/public/baseline/intake/${token}/upload`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setDocs((d) => [...d, { name: file.name }]);
      api.post("/public/baseline/track", { event: "intake_upload", session_id: token }).catch(() => {});
      toast.success(`Uploaded ${file.name}`);
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Upload failed"); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const submit = async () => {
    if (!cert) { toast.error("Please provide the final certification."); return; }
    setBusy(true);
    const intake = { firm, vendors: vendors.filter((v) => Object.keys(v).length), performance: perf, comments, certified: true };
    try {
      await api.post(`/public/baseline/intake/${token}`, { intake });
      api.post("/public/baseline/track", { event: "intake_submit", session_id: token }).catch(() => {});
      setState("submitted");
      toast.success("Confidential intake submitted");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not submit"); }
    finally { setBusy(false); }
  };

  if (state === "loading") return <Center><Loader2 className="h-10 w-10 animate-spin text-zinc-400" /></Center>;
  if (state === "unpaid") return <Center><p className="text-zinc-300">This confidential intake becomes available after your payment is confirmed.</p><Link to="/baseline-review" className="text-[#D5A44A] mt-4">Back to Baseline Review</Link></Center>;
  if (state === "notfound") return <Center><p className="text-zinc-300">This intake link is invalid or has expired.</p></Center>;
  if (state === "submitted") return (
    <Center>
      <CheckCircle2 className="h-14 w-14 text-[#0E7C7B]" />
      <h1 className="font-display text-3xl font-light mt-6">Intake received</h1>
      <p className="text-zinc-400 text-sm mt-3 max-w-md text-center">Thank you. Your review timeline will begin after RetainProof confirms the required information and documentation have been received. The expected delivery date will be communicated after intake review.</p>
      {info?.findings_available && (
        <a href={`${api.defaults.baseURL}/public/baseline/findings/${token}`} target="_blank" rel="noreferrer" className="mt-6 inline-flex items-center gap-2 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none px-5 h-11 text-sm font-medium transition-colors" data-testid="download-findings-submitted">
          <FileText className="h-4 w-4" /> Download Findings Report
        </a>
      )}
      <Link to="/" className="text-[#D5A44A] mt-6">Return to RetainProof</Link>
    </Center>
  );

  return (
    <div className="min-h-screen bg-[#0A1B2E] text-white font-body">
      <div className="grain-overlay" />
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-14">
        <Link to="/" className="inline-flex items-center gap-2 font-body font-bold text-xl mb-8"><img src="/retainproof-icon-dark.png" alt="RetainProof" className="h-7" /><span>Retain<span className="text-[#0E7C7B]">Proof</span></span></Link>
        <p className="font-mono-data text-[10px] uppercase tracking-widest text-[#D5A44A] flex items-center gap-1.5"><Lock className="h-3 w-3" />Confidential intake</p>
        <h1 className="font-display text-4xl font-light mt-2">{info?.firm_name} — Confidential Intake</h1>
        <p className="text-sm text-zinc-500 mt-2 max-w-2xl">Provide the available information and documentation needed for your Baseline Review. Your timeline begins once the required information is received and confirmed.</p>

        <Section title="Firm Information">
          <div className="grid sm:grid-cols-2 gap-3">{FIRM_FIELDS.map(([k, l]) => (<Fld key={k} l={l}><Input value={firm[k] || ""} onChange={upFirm(k)} className="rounded-none bg-white/5 border-white/15" /></Fld>))}</div>
        </Section>

        {vendors.map((_, i) => (
          <Section key={i} title={`Marketing Vendor ${i + 1}`} subtitle="Optional — up to three vendors">
            <div className="grid sm:grid-cols-2 gap-3">{VENDOR_FIELDS.map(([k, l]) => (<Fld key={k} l={l}><Input value={vendors[i][k] || ""} onChange={upVendor(i, k)} className="rounded-none bg-white/5 border-white/15" /></Fld>))}</div>
          </Section>
        ))}

        <Section title="Firm Performance Information">
          <div className="grid sm:grid-cols-2 gap-3">{PERF_FIELDS.map(([k, l]) => (<Fld key={k} l={l}><Input value={perf[k] || ""} onChange={upPerf(k)} className="rounded-none bg-white/5 border-white/15" /></Fld>))}</div>
        </Section>

        <Section title="Secure Document Uploads" subtitle="Invoices, agency & platform reports, call-tracking, intake, retained-matter and revenue reports, contracts, and other documentation.">
          <label className="flex flex-col items-center justify-center border border-dashed border-white/20 bg-white/5 py-8 cursor-pointer hover:bg-white/10 transition-colors" data-testid="intake-upload">
            {uploading ? <Loader2 className="h-6 w-6 animate-spin text-[#D5A44A]" /> : <UploadCloud className="h-6 w-6 text-[#D5A44A]" />}
            <span className="text-sm text-zinc-400 mt-2">{uploading ? "Uploading…" : "Click to upload a document (PDF, image, CSV — 25MB max)"}</span>
            <input type="file" className="hidden" onChange={uploadFile} disabled={uploading} />
          </label>
          {docs.length > 0 && (<ul className="mt-4 space-y-2">{docs.map((d, i) => (<li key={i} className="flex items-center gap-2 text-sm text-zinc-300"><FileText className="h-4 w-4 text-[#0E7C7B]" />{d.name}</li>))}</ul>)}
        </Section>

        <Section title="Comments or Special Instructions">
          <Textarea rows={3} value={comments} onChange={(e) => setComments(e.target.value)} className="rounded-none bg-white/5 border-white/15" />
        </Section>

        {info?.findings_available && (
          <Section title="Your Findings Report" subtitle="Your RetainProof executive findings report is ready to download.">
            <a href={`${api.defaults.baseURL}/public/baseline/findings/${token}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#D5A44A] text-black hover:bg-[#B8923D] rounded-none px-5 h-11 text-sm font-medium transition-colors" data-testid="download-findings">
              <FileText className="h-4 w-4" /> Download Findings Report
            </a>
          </Section>
        )}

        <label className="flex gap-2 text-xs text-zinc-400 mt-2"><input type="checkbox" checked={cert} onChange={(e) => setCert(e.target.checked)} data-testid="intake-cert" className="mt-0.5" />I certify that I am authorized to provide this information on behalf of the firm and understand that the accuracy of the RetainProof analysis depends on the accuracy and completeness of the information submitted.</label>

        <Button onClick={submit} disabled={busy} className="mt-6 w-full rounded-none bg-[#D5A44A] text-black hover:bg-[#B8923D] h-12" data-testid="intake-submit">
          {busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Submit Confidential Intake
        </Button>
        <p className="mt-3 text-[11px] text-zinc-500">Payment reserves the firm's production window. The review period begins after RetainProof confirms that the required information and documentation have been received. The expected delivery date will be communicated after intake review.</p>
      </div>
    </div>
  );
}

const Center = ({ children }) => (<div className="min-h-screen bg-[#0A1B2E] text-white font-body flex flex-col items-center justify-center p-6"><div className="grain-overlay" /><div className="relative z-10 flex flex-col items-center">{children}</div></div>);
const Section = ({ title, subtitle, children }) => (<div className="mt-8 border border-white/10 bg-[#0B1F33] p-6"><h2 className="font-display text-xl">{title}</h2>{subtitle && <p className="text-xs text-zinc-500 mt-1 mb-4">{subtitle}</p>}<div className={subtitle ? "" : "mt-4"}>{children}</div></div>);
const Fld = ({ l, children }) => (<div><Label className="text-[11px] uppercase tracking-widest font-mono-data text-zinc-500">{l}</Label><div className="mt-1">{children}</div></div>);
